// src/utils/productionLogger.js
/**
 * Production-grade logging and monitoring utility for subscription management system
 */

import performanceOptimizer from './performanceOptimizer';

// Get correct API base URL (same logic as api/client.js)
const getApiBaseUrl = () => {
  if (process.env.NODE_ENV === 'development') {
    return process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
  } else {
    // Use proxy approach for better security - Vercel forwards /api/* to backend
    return '/api';
  }
};

class ProductionLogger {
  constructor() {
    this.isProduction = process.env.NODE_ENV === 'production';
    this.isDevelopment = process.env.NODE_ENV === 'development';
    
    // Log levels
    this.levels = {
      ERROR: 0,
      WARN: 1,
      INFO: 2,
      DEBUG: 3
    };
    
    // Production log level (only ERROR and WARN)
    this.currentLevel = this.isProduction ? this.levels.WARN : this.levels.DEBUG;
    
    // Event counters for monitoring
    this.metrics = {
      subscriptionErrors: 0,
      tokenRefreshFailures: 0,
      apiFailures: 0,
      networkErrors: 0,
      webhookProcessingErrors: 0,
      userErrors: 0
    };
    
    // Error patterns for categorization
    this.errorPatterns = {
      network: ['network', 'timeout', 'connect', 'fetch', 'offline'],
      authentication: ['401', 'unauthorized', 'token', 'expired', 'invalid'],
      payment: ['payment', 'billing', 'stripe', 'invoice', 'card'],
      server: ['500', '502', '503', '504', 'internal', 'server'],
      user: ['400', 'validation', 'invalid input', 'missing field']
    };
    
    // Initialize monitoring
    this.initializeMonitoring();
  }

  /**
   * Initialize production monitoring
   */
  initializeMonitoring() {
    // Track unhandled promise rejections
    if (typeof window !== 'undefined') {
      window.addEventListener('unhandledrejection', (event) => {
        const normalizedReason = this.normalizeRejectionReason(event.reason);

        this.error(
          'Unhandled Promise Rejection',
          {
            reasonMessage: normalizedReason.message,
            reasonStack: normalizedReason.stack,
            reasonName: normalizedReason.name,
            url: window.location.href
          },
          { severity: 'high', category: 'frontend_runtime' }
        );
      });
      
      // Track JavaScript errors
      window.addEventListener('error', (event) => {
        this.error('JavaScript Error', {
          message: event.message,
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          error: event.error
        });
      });
      
      // Send metrics periodically in production
      if (this.isProduction) {
        setInterval(() => {
          this.sendMetricsToBackend();
        }, 5 * 60 * 1000); // Every 5 minutes
        
        // Send metrics before page unload
        window.addEventListener('beforeunload', () => {
          this.sendMetricsToBackend(true);
        });
      }
    }
  }

  /**
   * Log error with context and monitoring
   */
  error(message, context = {}, options = {}) {
    if (this.currentLevel < this.levels.ERROR) return;
    
    const logEntry = this.createLogEntry('ERROR', message, context, options);
    
    // Always log errors to console
    console.error('🚨 [ERROR]', message, context);
    
    // Categorize and count error
    this.categorizeError(context.error || context, context);
    
    // Send to monitoring service in production
    if (this.isProduction) {
      this.sendToMonitoringService(logEntry);
    }
    
    // Track performance impact
    if (context.endpoint) {
      performanceOptimizer.trackApiCall(context.endpoint, context.duration || 0);
    }
    
    return logEntry;
  }

  /**
   * Log warning with context
   */
  warn(message, context = {}) {
    if (this.currentLevel < this.levels.WARN) return;
    
    const logEntry = this.createLogEntry('WARN', message, context);
    
    console.warn('⚠️ [WARN]', message, context);
    
    if (this.isProduction) {
      this.sendToMonitoringService(logEntry);
    }
    
    return logEntry;
  }

  /**
   * Log info (development only)
   */
  info(message, context = {}) {
    if (this.currentLevel < this.levels.INFO) return;
    
    const logEntry = this.createLogEntry('INFO', message, context);
    
    if (this.isDevelopment) {
      console.info('ℹ️ [INFO]', message, context);
    }
    
    return logEntry;
  }

  /**
   * Log debug (development only)
   */
  debug(message, context = {}) {
    if (this.currentLevel < this.levels.DEBUG) return;
    
    const logEntry = this.createLogEntry('DEBUG', message, context);
    
    if (this.isDevelopment) {
      console.debug('🔍 [DEBUG]', message, context);
    }
    
    return logEntry;
  }

  /**
   * Create structured log entry
   */
  createLogEntry(level, message, context, options = {}) {
    return {
      timestamp: new Date().toISOString(),
      level,
      message,
      context: {
        ...context,
        url: typeof window !== 'undefined' ? window.location.href : null,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        sessionId: this.getSessionId(),
        userId: this.getUserId()
      },
      severity: options.severity || this.getSeverity(level, context),
      category: options.category || this.categorizeLog(context),
      environment: process.env.NODE_ENV,
      version: process.env.REACT_APP_VERSION || '1.0.0'
    };
  }

  /**
   * Get session identifier
   */
  getSessionId() {
    if (typeof window === 'undefined') return null;
    
    let sessionId = sessionStorage.getItem('sessionId');
    if (!sessionId) {
      sessionId = 'sess_' + Date.now() + '_' + Math.random().toString(36).substring(2, 11);
      sessionStorage.setItem('sessionId', sessionId);
    }
    return sessionId;
  }

  /**
   * Get user identifier (if available)
   */
  getUserId() {
    if (typeof localStorage === 'undefined') return null;
    
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return null;
      
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.sub || payload.user_id || null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Normalize unhandled promise rejection reason so it survives JSON.stringify
   */
  normalizeRejectionReason(reason) {
    if (!reason) {
      return { message: 'Unknown rejection', stack: null, name: 'UnknownError' };
    }

    if (reason instanceof Error) {
      return {
        message: String(reason.message || reason.toString()),
        stack: reason.stack ? String(reason.stack) : null,
        name: reason.name || 'Error'
      };
    }

    if (typeof reason === 'object') {
      return {
        message: JSON.stringify(reason),
        stack: null,
        name: reason.constructor && reason.constructor.name ? reason.constructor.name : 'Object'
      };
    }

    return {
      message: String(reason),
      stack: null,
      name: typeof reason
    };
  }

  /**
   * Categorize error for monitoring
   */
  categorizeError(error, context) {
    let category = 'unknown';
    const errorString = JSON.stringify(error).toLowerCase();
    
    // Categorize based on error patterns
    for (const [cat, patterns] of Object.entries(this.errorPatterns)) {
      if (patterns.some(pattern => errorString.includes(pattern))) {
        category = cat;
        break;
      }
    }
    
    // Increment counters
    switch (category) {
      case 'network':
        this.metrics.networkErrors++;
        break;
      case 'authentication':
        this.metrics.tokenRefreshFailures++;
        break;
      case 'payment':
      case 'server':
        this.metrics.subscriptionErrors++;
        break;
      case 'user':
        this.metrics.userErrors++;
        break;
      default:
        this.metrics.apiFailures++;
    }
    
    return category;
  }

  /**
   * Categorize log entry
   */
  categorizeLog(context) {
    if (context.operation) {
      if (context.operation.includes('subscription')) return 'subscription';
      if (context.operation.includes('token')) return 'authentication';
      if (context.operation.includes('payment')) return 'billing';
    }
    
    if (context.endpoint) {
      if (context.endpoint.includes('/billing/')) return 'billing';
      if (context.endpoint.includes('/auth/')) return 'authentication';
    }
    
    return 'general';
  }

  /**
   * Determine log severity
   */
  getSeverity(level, context) {
    if (level === 'ERROR') {
      // Critical errors that require immediate attention
      if (context.error?.response?.status >= 500) return 'critical';
      if (context.operation === 'subscription_failure') return 'critical';
      if (context.operation === 'payment_failure') return 'high';
      return 'medium';
    }
    
    if (level === 'WARN') {
      return 'low';
    }
    
    return 'info';
  }

  /**
   * Send metrics to backend monitoring service
   */
  async sendMetricsToBackend(isBeforeUnload = false) {
    if (!this.isProduction) return;
    
    const metrics = {
      timestamp: new Date().toISOString(),
      sessionId: this.getSessionId(),
      userId: this.getUserId(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      metrics: { ...this.metrics },
      performance: performanceOptimizer.getPerformanceReport()
    };
    
    try {
      const method = isBeforeUnload ? 'sendBeacon' : 'fetch';
      
      const apiUrl = `${getApiBaseUrl()}/monitoring/metrics`;
      
      if (method === 'sendBeacon' && navigator.sendBeacon) {
        navigator.sendBeacon(apiUrl, JSON.stringify(metrics));
      } else {
        // Make non-blocking call with timeout to prevent API delays
        fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(metrics),
          keepalive: isBeforeUnload,
          signal: AbortSignal.timeout(2000) // 2 second timeout
        }).catch(() => {
          // Silently fail - don't let monitoring interfere with user experience
        });
      }
      
      // Reset metrics after sending
      this.resetMetrics();
    } catch (error) {
      // Silently fail - monitoring should never break the app
    }
  }

  /**
   * Send log entry to external monitoring service
   */
  async sendToMonitoringService(logEntry) {
    if (!this.isProduction) return;
    
    try {
      // Use sendBeacon for critical errors to ensure delivery
      if (logEntry.severity === 'critical' && navigator.sendBeacon) {
        navigator.sendBeacon('/api/monitoring/logs', JSON.stringify(logEntry));
      } else {
        // Non-blocking fetch with timeout for other logs
        fetch('/api/monitoring/logs', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(logEntry),
          signal: AbortSignal.timeout(1500) // 1.5 second timeout
        }).catch(() => {
          // Silently fail - don't block user experience
        });
      }
    } catch (error) {
      // Silently fail - monitoring shouldn't break the app
    }
  }

  /**
   * Reset metrics counters
   */
  resetMetrics() {
    Object.keys(this.metrics).forEach(key => {
      this.metrics[key] = 0;
    });
  }

  /**
   * Log subscription operation
   */
  logSubscriptionOperation(operation, context = {}) {
    const logContext = {
      operation: `subscription_${operation}`,
      ...context
    };
    
    if (context.error) {
      this.error(`Subscription ${operation} failed`, logContext);
    } else {
      this.info(`Subscription ${operation} completed`, logContext);
    }
  }

  /**
   * Log token refresh operation
   */
  logTokenRefresh(success, context = {}) {
    const logContext = {
      operation: 'token_refresh',
      success,
      ...context
    };
    
    if (success) {
      this.info('Token refresh successful', logContext);
    } else {
      this.error('Token refresh failed', logContext, { severity: 'high' });
    }
  }

  /**
   * Log webhook processing
   */
  logWebhookProcessing(eventType, success, context = {}) {
    const logContext = {
      operation: 'webhook_processing',
      eventType,
      success,
      ...context
    };
    
    if (success) {
      this.info(`Webhook processed: ${eventType}`, logContext);
    } else {
      this.metrics.webhookProcessingErrors++;
      this.error(`Webhook processing failed: ${eventType}`, logContext, { 
        severity: 'high',
        category: 'webhook'
      });
    }
  }

  /**
   * Log API call with performance metrics
   */
  logApiCall(endpoint, method, duration, success, context = {}) {
    const logContext = {
      operation: 'api_call',
      endpoint,
      method,
      duration,
      success,
      ...context
    };
    
    if (success) {
      this.debug(`API call: ${method} ${endpoint}`, logContext);
    } else {
      this.error(`API call failed: ${method} ${endpoint}`, logContext);
    }
    
    // Track in performance optimizer
    performanceOptimizer.trackApiCall(endpoint, duration, context.cached);
  }

  /**
   * Get current metrics for dashboard
   */
  getMetrics() {
    return {
      ...this.metrics,
      sessionId: this.getSessionId(),
      timestamp: new Date().toISOString()
    };
  }

  /**
   * Create alert for critical issues
   */
  createAlert(message, context = {}, severity = 'high') {
    const alert = {
      id: 'alert_' + Date.now() + '_' + Math.random().toString(36).substring(2, 11),
      timestamp: new Date().toISOString(),
      message,
      context,
      severity,
      resolved: false
    };
    
    this.error(`ALERT: ${message}`, { alert: alert }, { severity });
    
    // In production, this would typically integrate with
    // alerting services like PagerDuty, Slack, etc.
    if (this.isProduction && severity === 'critical') {
      console.error('🚨 CRITICAL ALERT:', alert);
    }
    
    return alert;
  }
}

// Create singleton instance
const productionLogger = new ProductionLogger();

export default productionLogger;
